import PubSub from 'pubsub-js'
import rt from 'aldoo-ra/Realtime'

/**
 * Centralized Newsletter Realtime Controller
 *
 * Manages socket.io connections and publishes events via PubSub
 * so multiple components can subscribe without managing their own connections.
 *
 * Events published:
 * - 'newsletter:release_status_updated' - Release status changed with all data
 * - 'newsletter:release_deleted' - Release was deleted
 * - 'newsletter:release_rate_limited' - Release hit rate limit
 * - 'newsletter:release_rate_updated' - Release rate was updated
 * - 'newsletter:realtime_connected' - Socket connected
 * - 'newsletter:realtime_error' - Socket error occurred
 */
class NewsletterRealtimeController {
  constructor() {
    this.socket = null
    this.isSubscribed = false
    this.connectionAttempts = 0
    this.maxRetries = 3
  }

  /**
   * Initialize the realtime connection and set up event listeners
   */
  init() {
    if (this.socket) {
      return
    }

    try {
      this.socket = rt('newsletter')

      if (!this.socket) {
        console.error('❌ [NL RT] Failed to get newsletter socket')
        return
      }

      // Clear any existing listeners
      this.socket.off('connect')
      this.socket.off('connect_error')
      this.socket.off('disconnect')
      this.socket.off('newsletter:release_status_updated')
      this.socket.off('newsletter:release_deleted')
      this.socket.off('newsletter:release_rate_limited')
      this.socket.off('newsletter:release_rate_updated')

      // Connection events
      this.socket.on('connect', () => {
        this.connectionAttempts = 0
        this.subscribe()
        PubSub.publish('newsletter:realtime_connected', { connected: true })
      })

      this.socket.on('connect_error', (error) => {
        console.error('❌ [NL RT] Connection error:', error.message)
        this.connectionAttempts++

        if (this.connectionAttempts >= this.maxRetries) {
          PubSub.publish('newsletter:realtime_error', {
            error: 'Failed to connect after multiple attempts',
            attempts: this.connectionAttempts
          })
        }
      })

      this.socket.on('disconnect', (reason) => {
        this.isSubscribed = false
        PubSub.publish('newsletter:realtime_connected', { connected: false, reason })
      })

      // Newsletter events - republish via PubSub
      this.socket.on('newsletter:release_status_updated', (data) => {
        PubSub.publish('newsletter:release_status_updated', data)
      })

      this.socket.on('newsletter:release_deleted', (data) => {
        PubSub.publish('newsletter:release_deleted', data)
      })

      this.socket.on('newsletter:release_rate_limited', (data) => {
        PubSub.publish('newsletter:release_rate_limited', data)
      })

      this.socket.on('newsletter:release_rate_updated', (data) => {
        PubSub.publish('newsletter:release_rate_updated', data)
      })

      // If already connected, subscribe immediately
      if (this.socket.connected) {
        this.subscribe()
      }

    } catch (error) {
      console.error('❌ [NL RT] Failed to initialize:', error)
      PubSub.publish('newsletter:realtime_error', { error: error.message })
    }
  }

  /**
   * Subscribe to newsletter:releases room
   */
  subscribe() {
    if (!this.socket || this.isSubscribed) {
      return
    }

    this.socket.emit('newsletter:subscribe_releases', {}, (response) => {
      if (response?.success) {
        this.isSubscribed = true
      } else {
        console.error('❌ [NL RT] Failed to subscribe:', response)
      }
    })
  }

  /**
   * Unsubscribe from newsletter:releases room
   */
  unsubscribe() {
    if (!this.socket || !this.isSubscribed) {
      return
    }

    this.socket.emit('newsletter:unsubscribe_releases', {})
    this.isSubscribed = false
  }

  /**
   * Clean up socket connections and listeners
   */
  destroy() {
    if (!this.socket) {
      return
    }

    this.unsubscribe()

    this.socket.off('connect')
    this.socket.off('connect_error')
    this.socket.off('disconnect')
    this.socket.off('newsletter:release_status_updated')
    this.socket.off('newsletter:release_deleted')
    this.socket.off('newsletter:release_rate_limited')
    this.socket.off('newsletter:release_rate_updated')

    this.socket = null
    this.isSubscribed = false
  }
}

// Export singleton instance
const instance = new NewsletterRealtimeController()
export default instance
